<template>
  <div class="relative flex h-full flex-col text-white">
    <img
      src="/images/sign-in-background.webp"
      class="pointer-events-none absolute inset-0 h-full w-full object-cover"
      :alt="t('SIGN_IN_BACKGROUND')"
    />
    <!-- Dark overlay to darken the image -->
    <div class="pointer-events-none absolute inset-0 bg-black/60" />

    <div class="relative z-1 flex h-full flex-col p-6">
      <JigsawLogo class="h-5 w-16.5" />

      <div class="font-soehne flex flex-grow flex-col justify-center text-left">
        <h1 class="text-28 font-semibold">
          {{ $t('SIGN_IN_TO_GET_STARTED') }}
        </h1>
        <p class="text-16 text-secondary-90 mt-2">
          {{ $t('INSERT_EDIT_AND_UPDATE_JIGSAW_DIAGRAMS') }}
        </p>
      </div>

      <div class="mt-8 flex flex-col items-center gap-4">
        <Button
          variant="secondary"
          size="lg"
          class="w-full"
          @click="openSignIn()"
        >
          {{ $t('PUBLIC_SIGN_IN') }}
          <IconArrowRight16 />
        </Button>
        <div class="text-12 flex items-center gap-2">
          <IconOpenNewTab20 />
          <p>
            {{ $t('PUBLIC_OPENS_YOUR_BROWSER_TO_SIGN_IN_SECURELY') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { notify } from '@/components/AppNotification';
import {
  type OfficeDialogHandle,
  useOfficeDialog,
} from '@/composables/useOfficeDialog';
import { getStoredOfficeHref, isLoginSuccessMessage } from '@/utils';
import { SIGNED_OUT_QUERY_PARAM } from '@/common/constants';
import { onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { Button, JigsawLogo } from '@jigsaw/ui';
import { IconArrowRight16, IconOpenNewTab20 } from '@jigsaw/ui/iconography';

const { openDialog } = useOfficeDialog();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();

onMounted(async () => {
  if (!route.query[SIGNED_OUT_QUERY_PARAM]) {
    return;
  }
  // Drop the flag so a reload does not reopen the dialog.
  await router.replace({ query: {} });
  // Opened without a click, so Office on the web may block it. The Sign in button stays as the fallback.
  await openSignIn({ silent: true });
});

function redirectToOfficeApp(): void {
  const officeHref = getStoredOfficeHref();
  if (!officeHref) {
    notify(t('COULD_NOT_REDIRECT_TO_THE_WORKSPACE_PLEASE_TRY_AGAIN'), {
      type: 'error',
    });
    return;
  }

  try {
    window.location.replace(officeHref);
  } catch {
    notify(t('COULD_NOT_REDIRECT_TO_THE_WORKSPACE_PLEASE_TRY_AGAIN'), {
      type: 'error',
    });
  }
}

async function openSignIn({ silent = false } = {}): Promise<void> {
  let dialogHandle: OfficeDialogHandle | null = null;

  try {
    const result = await openDialog(
      `${window.location.origin}/sign-in-dialog`,
      {
        height: 65,
        width: 25,
        onMessage: (data) => {
          if (!isLoginSuccessMessage(data)) {
            return;
          }

          dialogHandle?.close();
          redirectToOfficeApp();
        },
      }
    );
    dialogHandle = result;
    if (!result) {
      throw new Error(t('COULD_NOT_OPEN_THE_SIGN_IN_WINDOW'));
    }
  } catch (err) {
    if (silent) {
      return;
    }
    const message =
      err instanceof Error
        ? err.message
        : t('COULD_NOT_OPEN_THE_SIGN_IN_WINDOW');
    notify(message, { type: 'error' });
  }
}
</script>
